import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import breakpoints from './Theme/breakpoints';

const imageContainerPropertyTypes = {
  height: PropTypes.string,
  maxHeight: PropTypes.string,
  objectFit: PropTypes.string,
  showOnlyOn: PropTypes.string,
  width: PropTypes.string,
};

type ImageContainerProperties = InferProps<typeof imageContainerPropertyTypes>;

const ImageContainer = styled.div<ImageContainerProperties>`
  ${({ width }) => (width && `
    width: ${width};
  `)};
  ${({ height }) => (height && `
    height: ${height};
  `)};

  /* stylelint-disable-next-line selector-type-no-unknown */
  progressive-img, img {
    ${({ width }) => (width && `
      width: ${width};
      --img-width: ${width};
    `)}
    ${({ objectFit }) => (objectFit && `
      object-fit: ${objectFit};
      --img-object-fit: ${objectFit};
    `)}
    ${({ height }) => (height && `
      height: ${height};
      --img-height: ${height};
    `)}
    ${({ maxHeight }) => (maxHeight && `
      max-height: ${maxHeight};
      --img-max-height: ${maxHeight};
    `)}
  }

  display: ${({ showOnlyOn }) => (showOnlyOn === 'desktop' ? 'none' : 'block')};

  @media (min-width: ${breakpoints.lg}px) {
    display: ${({ showOnlyOn }) => (showOnlyOn === 'mobile'
    ? 'none' : 'block')};
  }
`;

ImageContainer.propTypes = imageContainerPropertyTypes;

const NoScript = styled.noscript`
  min-width: 100%;
`;

const imagePropertyTypes = {
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  height: PropTypes.string,
  image: PropTypes.node.isRequired,
  maxHeight: PropTypes.string,
  objectFit: PropTypes.string,
  showOnlyOn: PropTypes.string,
  sizes: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  srcSet: PropTypes.string.isRequired,
  width: PropTypes.string,
};

type ImageProperties = InferProps<typeof imagePropertyTypes>;

const Image = ({
  alt,
  className,
  height = 'auto',
  image,
  maxHeight = 'none',
  objectFit = 'contain',
  showOnlyOn,
  sizes,
  src,
  srcSet,
  width = 'auto',
}: ImageProperties) => (
  <ImageContainer
    className={className ?? undefined}
    height={height}
    maxHeight={maxHeight}
    objectFit={objectFit}
    showOnlyOn={showOnlyOn}
    width={width}
  >
    {image}
    <NoScript>
      <img
        alt={alt}
        src={src}
        sizes={sizes}
        srcSet={srcSet}
      />
    </NoScript>
  </ImageContainer>
);

Image.propTypes = imagePropertyTypes;

export default Image;
